// frontend/src/components/LabReportUpload/ImageGalleryDialog.tsx

import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Grid,
  Card,
  CardMedia,
  Typography
} from '@mui/material';
import { NavigateBefore, NavigateNext, Close } from '@mui/icons-material';

interface ImageGalleryDialogProps {
  open: boolean;
  reportId: string;
  images: Array<{
    page_number: number;
    image_data_base64: string;
    format: string;
    width: number;
    height: number;
  }>;
  onClose: () => void;
}

export default function ImageGalleryDialog({
  open,
  reportId,
  images,
  onClose
}: ImageGalleryDialogProps) {
  const [currentPage, setCurrentPage] = useState(1);

  const handlePrevious = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const handleNext = () => {
    setCurrentPage((prev) => Math.min(images.length, prev + 1));
  };

  const currentImage = images.find((img) => img.page_number === currentPage);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Lab Report Images - Page {currentPage} of {images.length}
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Main image viewer */}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 500 }}>
          {currentImage && (
            <img
              src={`data:image/${currentImage.format.toLowerCase()};base64,${currentImage.image_data_base64}`}
              alt={`Page ${currentPage}`}
              style={{
                maxWidth: '100%',
                maxHeight: '600px',
                objectFit: 'contain'
              }}
            />
          )}
        </Box>

        {/* Page navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2 }}>
          <IconButton onClick={handlePrevious} disabled={currentPage === 1}>
            <NavigateBefore />
          </IconButton>

          <Typography sx={{ mx: 2 }}>
            {currentPage} / {images.length}
          </Typography>

          <IconButton onClick={handleNext} disabled={currentPage === images.length}>
            <NavigateNext />
          </IconButton>
        </Box>

        {/* Thumbnail strip */}
        {images.length > 1 && (
          <Grid container spacing={1} sx={{ mt: 2 }}>
            {images.map((img) => (
              <Grid item xs={2} key={img.page_number}>
                <Card
                  onClick={() => setCurrentPage(img.page_number)}
                  sx={{
                    cursor: 'pointer',
                    border: img.page_number === currentPage ? '2px solid #1976d2' : '1px solid #ccc'
                  }}
                >
                  <CardMedia
                    component="img"
                    image={`data:image/${img.format.toLowerCase()};base64,${img.image_data_base64}`}
                    alt={`Page ${img.page_number}`}
                    sx={{ height: 100, objectFit: 'cover' }}
                  />
                  <Typography variant="caption" align="center" display="block" sx={{ p: 0.5 }}>
                    Page {img.page_number}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
